/**
 * sourceFaviconUtils — frontend helpers for data.sourceFavicons.
 *
 * sourceFavicons is a { [host]: { favicon: string, name: string|null } } map
 * written to every lead doc by propertyIngestionV2 (Phase 2+). Phase 1 used
 * bare URL strings as values; these helpers accept both shapes so pages that
 * render before a full re-ingest are not broken.
 *
 * Both helpers are pure — safe to call with undefined/null sourceFavicons.
 */

/**
 * Returns the Google Favicon API URL for a hostname, or null.
 *
 * @param {string|null}  host            — hostname with www. already stripped
 * @param {Object|null}  sourceFavicons  — lead.sourceFavicons map
 * @returns {string|null}
 */
export function getFaviconSrc(host, sourceFavicons) {
    if (!host || !sourceFavicons) return null;
    const entry = sourceFavicons[host];
    if (!entry) return null;
    // Phase 1 shape: string; Phase 2 shape: { favicon, name }
    if (typeof entry === 'string') return entry;
    return (entry && typeof entry.favicon === 'string') ? entry.favicon : null;
}

/**
 * Returns the brand name for a hostname, falling back to the hostname itself.
 *
 * @param {string|null}  host            — hostname with www. already stripped
 * @param {Object|null}  sourceFavicons  — lead.sourceFavicons map
 * @returns {string}
 */
export function getSiteName(host, sourceFavicons) {
    if (!host) return host || '';
    const entry = sourceFavicons?.[host];
    // Phase 2 shape: { favicon, name }
    const name = (entry && typeof entry === 'object' && typeof entry.name === 'string')
        ? entry.name
        : null;
    return name || host;
}

/**
 * Extract a www-stripped, lowercased hostname from a URL string, or null when
 * the input is not a parseable absolute URL. Matches the host-key convention
 * used by buildSourceFavicons on the backend.
 *
 * @param {string|null} url
 * @returns {string|null}
 */
export function hostFromUrl(url) {
    if (!url || typeof url !== 'string') return null;
    try {
        return new URL(url).hostname.toLowerCase().replace(/^www\./, '');
    } catch (_) {
        return null;
    }
}

/**
 * Build a Google Favicon API URL for a hostname. Use when no sourceFavicons map
 * is available but a known-good website host is in hand (e.g. CRM / folder
 * click-out links). Mirrors FAVICON_BASE in
 * functions/propertyIngestionV2/utils/sourceFaviconUtils.js.
 *
 * @param {string|null} host — hostname (www. already stripped)
 * @returns {string|null}
 */
export function faviconUrlForHost(host) {
    if (!host || typeof host !== 'string') return null;
    return `https://www.google.com/s2/favicons?domain=${host}&sz=64`;
}

// Personal / free email providers — a sender on one of these has no company
// domain we can brand from, so their favicon would just be the provider's logo
// (Gmail's "M", etc.), not the wholesaler's. Mirrors the email-provider subset
// of EXCLUDED_HOST_PATTERNS in the backend sourceFaviconUtils.js.
const PERSONAL_EMAIL_HOSTS = new Set([
    // Major free webmail
    'gmail.com', 'googlemail.com',
    'outlook.com', 'hotmail.com', 'live.com', 'msn.com',
    'outlook.co.uk', 'hotmail.co.uk', 'live.co.uk',
    'yahoo.com', 'yahoo.co.uk', 'ymail.com', 'rocketmail.com',
    'aol.com', 'aim.com',
    'icloud.com', 'me.com', 'mac.com',
    'proton.me', 'protonmail.com', 'pm.me',
    'gmx.com', 'gmx.net', 'mail.com', 'zoho.com',
    'hey.com', 'fastmail.com', 'yandex.com', 'mail.ru',
    // US ISP / cable / telco mailboxes — a sender here would otherwise surface
    // the ISP's logo (Comcast, AT&T, …) as if it were their company brand.
    'comcast.net', 'xfinity.com',
    'att.net', 'sbcglobal.net', 'bellsouth.net', 'ameritech.net', 'pacbell.net', 'swbell.net', 'flash.net', 'prodigy.net',
    'verizon.net', 'frontier.com', 'frontiernet.net',
    'cox.net',
    'charter.net', 'twc.com', 'rr.com', 'roadrunner.com', 'spectrum.net',
    'optonline.net', 'optimum.net',
    'earthlink.net', 'juno.com', 'netzero.net', 'netzero.com',
    'windstream.net', 'centurylink.net', 'embarqmail.com', 'q.com',
    'suddenlink.net', 'mediacombb.net', 'cableone.net',
]);

/**
 * Build a Google Favicon API URL from a sender's email domain, for use as a
 * sender company logo when no classifier-detected logo image is available
 * (resolveSenderLogo's fallback). Returns null for personal/free email hosts
 * (gmail/outlook/etc.) — those have no brandable company domain.
 *
 * @param {string|null} email — raw sender email address
 * @returns {string|null} favicon URL at sz=128, or null
 */
export function getSenderDomainFavicon(email) {
    if (!email || typeof email !== 'string') return null;
    const at = email.lastIndexOf('@');
    if (at < 0) return null;
    const host = email.slice(at + 1).trim().toLowerCase().replace(/^www\./, '');
    if (!host || !host.includes('.')) return null;
    if (PERSONAL_EMAIL_HOSTS.has(host)) return null;
    return `https://www.google.com/s2/favicons?domain=${host}&sz=128`;
}
